import { useMemo, useState } from 'react';
import {
  Check,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Settings,
  Plus,
  Edit2,
  Trash2,
} from 'lucide-react';
import type { Artifact } from '@/types';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chatStore';

interface ConfigPreviewProps {
  artifact: Artifact;
}

interface ConfigChange {
  type: 'create' | 'update' | 'delete';
  resource: string;
  name: string;
  details?: Record<string, unknown>;
  warnings?: string[];
}

interface ConfigData {
  summary: string;
  changes: ConfigChange[];
  warnings?: string[];
}

export function ConfigPreview({ artifact }: ConfigPreviewProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const sendMessage = useChatStore((state) => state.sendMessage);
  const isStreaming = useChatStore((state) => state.isStreaming);

  const handleApprove = () => {
    if (!isStreaming) {
      sendMessage('Approve');
    }
  };

  const handleCancel = () => {
    if (!isStreaming) {
      sendMessage('Cancel');
    }
  };

  const config = useMemo<ConfigData>(() => {
    try {
      const parsed = JSON.parse(artifact.content) as Partial<ConfigData>;
      return {
        summary: parsed.summary ?? artifact.content,
        changes: parsed.changes ?? [],
        warnings: parsed.warnings,
      };
    } catch {
      return {
        summary: artifact.content,
        changes: [],
      };
    }
  }, [artifact.content]);

  const toggleExpand = (index: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const getIcon = (type: ConfigChange['type']) => {
    switch (type) {
      case 'create':
        return <Plus className="h-4 w-4 text-green-400" />;
      case 'update':
        return <Edit2 className="h-4 w-4 text-blue-400" />;
      case 'delete':
        return <Trash2 className="h-4 w-4 text-red-400" />;
    }
  };

  const getTypeLabel = (type: ConfigChange['type']) => {
    switch (type) {
      case 'create':
        return 'Create';
      case 'update':
        return 'Update';
      case 'delete':
        return 'Delete';
    }
  };

  const getTypeBadgeClass = (type: ConfigChange['type']) => {
    switch (type) {
      case 'create':
        return 'bg-green-900/30 text-green-400';
      case 'update':
        return 'bg-blue-900/30 text-blue-400';
      case 'delete':
        return 'bg-red-900/30 text-red-400';
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-3">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-purple-400" />
          <span className="font-medium text-gray-100">
            {artifact.title ?? 'Configuration Preview'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {config.changes.length} change{config.changes.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Summary */}
      {config.summary && (
        <div className="border-b border-gray-700 px-4 py-3">
          <p className="text-sm text-gray-400">
            {config.summary}
          </p>
        </div>
      )}

      {/* Warnings */}
      {config.warnings && config.warnings.length > 0 && (
        <div className="border-b border-gray-700 bg-yellow-900/20 px-4 py-3">
          <div className="flex items-start gap-2 text-sm text-yellow-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <div className="font-medium">Warnings</div>
              <ul className="mt-1 list-disc pl-4">
                {config.warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Changes list */}
      {config.changes.length > 0 ? (
        <div className="divide-y divide-gray-700">
          {config.changes.map((change, index) => (
            <div key={index} className="px-4 py-3">
              <button
                onClick={() => toggleExpand(index)}
                className="flex w-full items-center gap-3 text-left"
              >
                {change.details ? (
                  expandedItems.has(index) ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )
                ) : (
                  <div className="w-4" />
                )}
                {getIcon(change.type)}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-xs font-medium',
                        getTypeBadgeClass(change.type)
                      )}
                    >
                      {getTypeLabel(change.type)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {change.resource}
                    </span>
                  </div>
                  <div className="mt-0.5 font-medium text-gray-100">{change.name}</div>
                </div>
                {change.warnings && change.warnings.length > 0 && (
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                )}
              </button>

              {/* Expanded details */}
              {expandedItems.has(index) && change.details && (
                <div className="mt-3 ml-11 rounded-lg bg-gray-900 p-3">
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(change.details).map(([key, value]) => (
                        <tr key={key}>
                          <td className="py-1 pr-4 font-medium text-gray-400">
                            {key}
                          </td>
                          <td className="py-1 text-gray-200">
                            {typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {change.warnings && change.warnings.length > 0 && (
                    <div className="mt-2 rounded bg-yellow-900/20 p-2 text-xs text-yellow-200">
                      <div className="flex items-center gap-1 font-medium">
                        <AlertTriangle className="h-3 w-3" />
                        Warning
                      </div>
                      <ul className="mt-1 list-disc pl-4">
                        {change.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-sm text-gray-400">
          No configuration changes
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-3 border-t border-gray-700 px-4 py-3">
        <button
          onClick={handleCancel}
          disabled={isStreaming}
          className="flex items-center gap-2 rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X className="h-4 w-4" />
          Cancel
        </button>
        <button
          onClick={handleApprove}
          disabled={isStreaming}
          className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check className="h-4 w-4" />
          Approve
        </button>
      </div>
    </div>
  );
}
